import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from 'primereact/card';
import { useMenuCategory } from '../contexts/MenuCategoryContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../hooks/usePermissions';
import './MenuCategory.scss';
import NoticeBanner from './NoticeBanner';
import useMenuBadges from '../hooks/useMenuBadges';
import { Roles } from '../utils/roleEnums';

const MenuCategory = () => {
    const noticeItems = [
        { id: '1', level: 'warning', i18nKey: 'notices.maintenanceTonight', params: { time: '23:00' } },
        { id: '2', level: 'info', i18nKey: 'notices.newFeature', params: { name: 'Warranty Reports' } },
        { id: '3', level: 'error', i18nKey: 'notices.apiDown', params: { region: 'EU' } }
    ];
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedCategory, setSelectedCategory } = useMenuCategory();
    const { th } = useLanguage();
    const { filterMenuItems, userRoles } = usePermissions();
    const [categories, setCategories] = useState([]);
    const [hoveredCard, setHoveredCard] = useState(null);
    const [itemsPerRow, setItemsPerRow] = useState(4); // Varsayılan 4 sütun
    const { getBadge } = useMenuBadges();
    const isServiceBulletinUser = userRoles?.includes(Roles.SERVICE_BULLETIN_USER);

    // Statik kartlar - tıklanabilir değil
    const allStaticCards = [
        {
            id: 'technical-training',
            title: th('staticCards.technicalTraining', { fallback: 'Teknik Eğitim' }),
            icon: 'pi pi-book',
            color: 'transparent',
            isStatic: true
        },
        {
            id: 'backclaim',
            title: th('staticCards.backclaim', { fallback: 'Geri Talep' }),
            icon: 'pi pi-file-edit',
            color: 'transparent',
            isStatic: true
        },
        {
            id: 'tender-tracking',
            title: th('staticCards.tenderTracking', { fallback: 'İhale Takip' }),
            icon: 'pi pi-clipboard',
            color: 'transparent',
            isStatic: true
        }
    ];

    // Statik kartları rol kontrolü ile filtrele
    // RegionalManager ,SSHManager, SSHDirector, SSHGeneralManager ve CEO rollerini hariç tut
    const staticCards = useMemo(() => {
        const excludedRoles = ['RegionalManager', 'SSHManager', 'SSHDirector', 'SSHGeneralManager', 'CEO', 'KarsanSSH', 'Dealer', 'ServiceDealer', 'SalesDealer', 'VehicleSalesDealer'];

        if (isServiceBulletinUser) {
            return [];
        }

        if (!userRoles || userRoles.length === 0) {
            return allStaticCards;
        }

        const hasExcludedRole = userRoles.some((role) => excludedRoles.includes(role));

        // Eğer kullanıcı hariç tutulan rollerden birine sahipse, statik kartları gösterme
        if (hasExcludedRole) {
            return [];
        }

        return allStaticCards;
    }, [userRoles, th, isServiceBulletinUser]);

    useEffect(() => {
        // Kategorileri her zaman yükle (özellikle Ana Menü'ye döndüğümüzde)
        const loadCategories = () => {
            fetch('/data/menuData.json', { cache: 'no-cache' })
                .then((response) => response.json())
                .then((data) => {
                    const filteredData = filterMenuItems(data);
                    if (filteredData && filteredData.length > 0 && filteredData[0].children) {
                        // Ana menünün children'ları kategorilerimiz
                        const visibleCategories = isServiceBulletinUser ? filteredData[0].children.filter((category) => category.id === 'warranty-management') : filteredData[0].children;

                        const menuCategories = visibleCategories.map((category) => ({
                            id: category.id,
                            title: th(category.translationKey, { fallback: category.title }),
                            translationKey: category.translationKey,
                            icon: getCategoryIcon(category.id),
                            color: getCategoryColor(category.id),
                            firstPage: getFirstPage(category),
                            externalLink: category.externalLink
                        }));
                        setCategories(menuCategories);
                    }
                })
                .catch((error) => {
                    console.error('Error loading menu categories:', error);
                });
        };

        loadCategories();
    }, [filterMenuItems, th, selectedCategory, location.state, isServiceBulletinUser]);

    // Responsive: 768px ve altında 2 sütun, üstünde 4 sütun
    useEffect(() => {
        const handleResize = () => {
            setItemsPerRow(window.innerWidth <= 768 ? 2 : 4);
        };

        handleResize(); // İlk yüklemede kontrol et
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getCategoryIcon = (categoryId, isHovered = false) => {
        const iconMap = {
            management: 'management', // PNG dosya adı (hover durumunda sonuna "Hover" eklenecek)
            'dealer-management': 'dealer-management', // dealer-management.png + dealer-managementHover.png
            'warranty-management': 'warranty-management', // warranty-management.png + warranty-managementHover.png
            reports: 'pi pi-chart-bar',
            'order-management': 'order-management', // order-management.png + order-managementHover.png
            'full-service-invoices': 'full-service-invoices',
            announcements: 'announcements',
            'service-appointment': 'service-appointment',
            'credit-card-collection': 'credit-card-collection',
            'vehicle-sales': 'pi pi-car',
            finance: 'pi pi-wallet',
            pada5: '/padaLogo.png',
            'learning-management-system': 'pi pi-verified',
            'training-library': 'pi pi-folder-open',
            // Static kartlar
            'technical-training': 'technical-training', // technical-training.png + technical-trainingHover.png
            'technical-support': 'technical-support',
            backclaim: 'backclaim',
            'tender-tracking': 'tender-tracking'
        };

        const baseIcon = iconMap[categoryId] || 'pi pi-circle';

        // Eğer icon bir path değilse (pi icon ise) direkt döndür
        if (baseIcon.startsWith('pi ') || baseIcon.startsWith('/')) {
            return baseIcon;
        }

        // PNG için dinamik path oluştur
        const iconName = isHovered ? `${baseIcon}Hover` : baseIcon;
        return `/layout/images/menu/${iconName}.png`;
    };

    const getCategoryColor = (categoryId) => {
        // Altıgen tasarımda renk CSS'te ayarlandığı için burada kullanılmıyor
        const colorMap = {
            management: 'transparent',
            'dealer-management': 'transparent',
            'warranty-management': 'transparent',
            reports: 'transparent',
            'order-management': 'transparent',
            'full-service-invoices': 'transparent',
            announcements: 'transparent',
            'service-appointment': 'transparent',
            'credit-card-collection': 'transparent',
            'vehicle-sales': 'transparent',
            pada5: 'transparent',
            'learning-management-system': 'transparent',
            'training-library': 'transparent',
            finance: 'transparent'
        };
        return colorMap[categoryId] || 'transparent';
    };

    const getFirstPage = (category) => {
        // Kategorinin ilk sayfasını bul
        if (!category.children || category.children.length === 0) {
            return category.link || '/';
        }

        // Recursive olarak ilk link'i bul
        const findFirstLink = (items) => {
            for (const item of items) {
                if (item.link || item.path) {
                    return item.link || item.path;
                }
                if (item.children && item.children.length > 0) {
                    const childLink = findFirstLink(item.children);
                    if (childLink) return childLink;
                }
            }
            return null;
        };

        return findFirstLink(category.children) || '/';
    };

    const handleCategoryClick = (category) => {
        // Statik kartlar için hiçbir aksiyon alma
        if (category.isStatic) {
            return;
        }

        // Eğer dış link varsa, yeni sekmede aç
        if (category.externalLink) {
            window.open(category.externalLink, '_blank', 'noopener,noreferrer');
            return;
        }

        setSelectedCategory(category.id);
        // İlk sayfaya yönlendir
        navigate(category.firstPage);
    };

    return (
        <div className="menu-category-page">
            <div className="menu-category-header">
                <img src="/logorenkli.png" alt="Karsan Logo" className="menu-category-logo" style={{ height: '4rem', width: 'auto' }} />
            </div>

            {/* <div className="menu-category-banner">
                <NoticeBanner sticky autoScroll scrollInterval={3000} showDots />
            </div> */}

            <div className="menu-category-grid">
                {categories.map((category, index) => {
                    const rowNumber = Math.floor(index / itemsPerRow);
                    const isOffsetRow = rowNumber % 2 === 1;
                    const isFirstInRow = index % itemsPerRow === 0;
                    const isSecondInRow = index % itemsPerRow === 1;
                    const isHovered = hoveredCard === category.id;
                    const displayIcon = getCategoryIcon(category.id, isHovered);

                    // Offset satırın hem ilk hem ikinci elemanı
                    const offsetClass = isOffsetRow && (isFirstInRow ? 'offset-row-first' : isSecondInRow ? 'offset-row-second' : '');
                    const badgeCount = getBadge(category.id);

                    return (
                        <div key={category.id} className={`menu-category-card-wrapper ${offsetClass}`} onClick={() => handleCategoryClick(category)} onMouseEnter={() => setHoveredCard(category.id)} onMouseLeave={() => setHoveredCard(null)}>
                            <Card className="menu-category-card">
                                <div className="menu-category-content">
                                    <div className="inner-hexagon"></div>
                                    <div className="hexagon-dots">
                                        <span className="hex-dot dot-1"></span>
                                        <span className="hex-dot dot-2"></span>
                                        <span className="hex-dot dot-3"></span>
                                        <span className="hex-dot dot-4"></span>
                                        <span className="hex-dot dot-5"></span>
                                        <span className="hex-dot dot-6"></span>
                                    </div>
                                    <div className="menu-category-icon-wrapper">
                                        {displayIcon && displayIcon.startsWith('/') ? <img src={displayIcon} alt={category.title} className="menu-category-icon-image" /> : <i className={`${displayIcon} menu-category-icon`}></i>}
                                        {/* Badge - sadece announcements kartı için */}
                                        {badgeCount > 0 && (
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    top: '-6px',
                                                    right: '-6px',
                                                    backgroundColor: '#ef4444',
                                                    color: '#fff',
                                                    borderRadius: '50%',
                                                    minWidth: '30px',
                                                    height: '30px',
                                                    fontSize: '19px',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '0 4px',
                                                    lineHeight: 1,
                                                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                                                    zIndex: 10,
                                                    pointerEvents: 'none'
                                                }}
                                            >
                                                {badgeCount > 99 ? '99+' : badgeCount}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className={`menu-category-name${category.title && category.title.length > 24 ? ' long-title' : ''}`}>{category.title}</h3>
                                </div>
                            </Card>
                        </div>
                    );
                })}
                {staticCards.map((card, index) => {
                    const totalIndex = categories.length + index;
                    const rowNumber = Math.floor(totalIndex / itemsPerRow);
                    const isOffsetRow = rowNumber % 2 === 1;
                    const isFirstInRow = totalIndex % itemsPerRow === 0;
                    const isSecondInRow = totalIndex % itemsPerRow === 1;
                    const isHovered = hoveredCard === card.id;
                    const displayIcon = getCategoryIcon(card.id, isHovered);

                    // Offset satırın hem ilk hem ikinci elemanı
                    const offsetClass = isOffsetRow && (isFirstInRow ? 'offset-row-first' : isSecondInRow ? 'offset-row-second' : '');

                    return (
                        <div key={card.id} className={`menu-category-card-wrapper static-card ${offsetClass}`} onClick={() => handleCategoryClick(card)} onMouseEnter={() => setHoveredCard(card.id)} onMouseLeave={() => setHoveredCard(null)}>
                            <Card className="menu-category-card">
                                <div className="menu-category-content">
                                    <div className="inner-hexagon"></div>
                                    <div className="hexagon-dots">
                                        <span className="hex-dot dot-1"></span>
                                        <span className="hex-dot dot-2"></span>
                                        <span className="hex-dot dot-3"></span>
                                        <span className="hex-dot dot-4"></span>
                                        <span className="hex-dot dot-5"></span>
                                        <span className="hex-dot dot-6"></span>
                                    </div>
                                    <div className="menu-category-icon-wrapper">
                                        {displayIcon && displayIcon.startsWith('/') ? <img src={displayIcon} alt={card.title} className="menu-category-icon-image" /> : <i className={`${displayIcon} menu-category-icon`}></i>}
                                    </div>
                                    <h3 className={`menu-category-name${card.title && card.title.length > 24 ? ' long-title' : ''}`}>{card.title}</h3>
                                </div>
                            </Card>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MenuCategory;
